import { StoreOptions } from 'vuex';
import { StoreInitData, InitResponse, UpdatePageProperties, GeneralState, ExportStatusObject, ExportType } from '@/models/Interfaces';
import { Project, VisitedSteps } from '@/models/Project';
import { Newsletter } from '@/models/Newsletter';
import _ from 'lodash';
import SelectOption, { SelectOptionWithData } from '@/models/SelectOption';
import { translateList, translateListPrefix, translateListWithData } from '@/i18n';
import * as Enums from '@/models/enums';
import { ContactFieldMapping, ContactFilter, CustomListValueSaveResource } from '@/models/Contacts';
import { api } from '@/services/Api';
import Helper from '@/services/Helper';
import Swal from 'sweetalert2';
import Q from 'q';
import * as RefData from '@/models/RefData';

const storeState = new GeneralState();

const fieldTypes = [
    new SelectOption(Enums.FieldTypeEnum.Text, 'Text'),
    new SelectOption(Enums.FieldTypeEnum.Number, 'Number'),
    new SelectOption(Enums.FieldTypeEnum.Boolean, 'Boolean'),
    new SelectOption(Enums.FieldTypeEnum.Datetime, 'Datetime'),
    new SelectOption(Enums.FieldTypeEnum.SelectList, 'SelectList'),
    new SelectOption(Enums.FieldTypeEnum.MultiSelectList, 'MultiSelectList'),
];
const genders = [new SelectOption(Enums.GenderEnum.Male, 'Male'), new SelectOption(Enums.GenderEnum.Female, 'Female')];

const generalFields = [
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.Email, Enums.ContactFilterFieldEnum.Email, Enums.FieldTypeEnum.Text),
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.Mobile, Enums.ContactFilterFieldEnum.Mobile, Enums.FieldTypeEnum.Text),
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.FirstName, Enums.ContactFilterFieldEnum.FirstName, Enums.FieldTypeEnum.Text),
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.LastName, Enums.ContactFilterFieldEnum.LastName, Enums.FieldTypeEnum.Text),
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.Birthday, Enums.ContactFilterFieldEnum.Birthday, Enums.FieldTypeEnum.Datetime),
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.Gender, Enums.ContactFilterFieldEnum.Gender, Enums.FieldTypeEnum.Text),
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.Address, Enums.ContactFilterFieldEnum.Address, Enums.FieldTypeEnum.Text),
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.City, Enums.ContactFilterFieldEnum.City, Enums.FieldTypeEnum.Text),
    new SelectOptionWithData(
        Enums.ContactFilterFieldEnum.UnsubscribedEmail,
        Enums.ContactFilterFieldEnum.UnsubscribedEmail,
        Enums.FieldTypeEnum.Boolean
    ),
    new SelectOptionWithData(
        Enums.ContactFilterFieldEnum.UnsubscribedMobile,
        Enums.ContactFilterFieldEnum.UnsubscribedMobile,
        Enums.FieldTypeEnum.Boolean
    ),
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.Tags, Enums.ContactFilterFieldEnum.Tags, Enums.FieldTypeEnum.MultiSelectList),
    new SelectOptionWithData(Enums.ContactFilterFieldEnum.Groups, Enums.ContactFilterFieldEnum.Groups, Enums.FieldTypeEnum.MultiSelectList),
];

const operationsPerType: { [key in Enums.ContactFilterTypeEnum]: SelectOption[] } = {
    Text: [
        Enums.ContactFilterOperationEnum.IsNull,
        Enums.ContactFilterOperationEnum.IsNotNull,
        Enums.ContactFilterOperationEnum.Is,
        Enums.ContactFilterOperationEnum.IsNot,
        Enums.ContactFilterOperationEnum.Contains,
        Enums.ContactFilterOperationEnum.ContainsNot,
    ].map((x) => new SelectOption(String(x), String(x))),
    Number: [
        [Enums.ContactFilterOperationEnum.IsNull, ''],
        [Enums.ContactFilterOperationEnum.IsNotNull, ''],
        [Enums.ContactFilterOperationEnum.Is, ''],
        [Enums.ContactFilterOperationEnum.IsNot, ''],
        [Enums.ContactFilterOperationEnum.Contains, ''],
        [Enums.ContactFilterOperationEnum.ContainsNot, ''],
        [Enums.ContactFilterOperationEnum.IsBefore, 'Number.IsBefore'],
        [Enums.ContactFilterOperationEnum.IsAfter, 'Number.IsAfter'],
        [Enums.ContactFilterOperationEnum.InRange, 'Number.InRange'],
    ].map((x) => new SelectOption(String(x[0]), x[1] || String(x[0]))),
    Datetime: [
        [Enums.ContactFilterOperationEnum.IsNull, ''],
        [Enums.ContactFilterOperationEnum.IsNotNull, ''],
        [Enums.ContactFilterOperationEnum.Is, ''],
        [Enums.ContactFilterOperationEnum.IsNot, ''],
        [Enums.ContactFilterOperationEnum.IsBefore, ''],
        [Enums.ContactFilterOperationEnum.IsAfter, ''],
        [Enums.ContactFilterOperationEnum.InRange, ''],
        [Enums.ContactFilterOperationEnum.Relative, ''],
    ].map((x) => new SelectOption(String(x[0]), x[1] || String(x[0]))),
    Boolean: [Enums.ContactFilterOperationEnum.Is].map((x) => new SelectOption(String(x), String(x))),
    Action: [
        Enums.ContactFilterOperationEnum.ActionClickLPLink,
        Enums.ContactFilterOperationEnum.ActionNotClickLPLink,
        Enums.ContactFilterOperationEnum.ActionClickEmailLink,
        Enums.ContactFilterOperationEnum.ActionNotClickEmailLink,
        Enums.ContactFilterOperationEnum.ActionOpenLP,
        Enums.ContactFilterOperationEnum.ActionNotOpenLP,
        Enums.ContactFilterOperationEnum.ActionOpenEmail,
        Enums.ContactFilterOperationEnum.ActionNotOpenEmail,
        Enums.ContactFilterOperationEnum.ActionSentEmail,
        Enums.ContactFilterOperationEnum.ActionNotSentEmail,
        Enums.ContactFilterOperationEnum.ActionSubmitForm,
        Enums.ContactFilterOperationEnum.ActionNotSubmitForm,
        Enums.ContactFilterOperationEnum.ActionAutomationStarted,
        Enums.ContactFilterOperationEnum.ActionAutomationNotStarted,
        Enums.ContactFilterOperationEnum.ActionAutomationEnded,
        Enums.ContactFilterOperationEnum.ActionAutomationNotEnded,
        Enums.ContactFilterOperationEnum.ActionClickSmsLink,
        Enums.ContactFilterOperationEnum.ActionNotClickSmsLink,
    ].map((x) => new SelectOption(String(x), String(x))),
    SelectList: [
        Enums.ContactFilterOperationEnum.IsNull,
        Enums.ContactFilterOperationEnum.IsNotNull,
        Enums.ContactFilterOperationEnum.Is,
        Enums.ContactFilterOperationEnum.IsNot,
    ].map((x) => new SelectOption(String(x), String(x))),
    MultiSelectList: [Enums.ContactFilterOperationEnum.And, Enums.ContactFilterOperationEnum.Or].map(
        (x) => new SelectOption(String(x), String(x))
    ),
};

export interface ContactFieldOptions {
    FieldType: Enums.ContactFilterTypeEnum;
    Keyword: string;
    CustomListId?: number | null;
    CustomListOptions?: SelectOption[];
    ColumnName: string;
}
export const generalStore: StoreOptions<GeneralState> = {
    state: storeState,
    getters: {
        CurrentLanguage: (state): string => state.Lang,
        IsRTL: ({ Lang }) => Lang && ['he', 'he-il'].includes(Lang.toLowerCase()),
        AllContactFields: ({ GeneralContactFields }, { ContactFields }) => [
            ...GeneralContactFields.map(
                (f) =>
                    new SelectOptionWithData(f.id, f.text, {
                        FieldType: f.data,
                        Keyword: 'ContactField:' + f.id,
                        ColumnName: Helper.toSnakeCase(f.id as string),
                    })
            ),
            ...ContactFields.map(
                (f: ContactFieldMapping) =>
                    new SelectOptionWithData<ContactFieldOptions>(f.Field, f.Name, {
                        FieldType: Enums.ContactFilterTypeEnum[f.FieldType as keyof typeof Enums.ContactFilterTypeEnum],
                        Keyword: 'ContactField:' + f.Name,
                        CustomListId: f.CustomListId,
                        CustomListOptions: f.CustomListOptions?.map((x) => new SelectOption(x.Id, x.Value)),
                        ColumnName: Helper.toSnakeCase(f.Field),
                    })
            ),
        ],
        Pages: ({ Pages }) => Pages.map((f) => new SelectOptionWithData(f.Id, f.Name, f)),
        Newsletters: ({ Newsletters }) => Newsletters.map((f) => new SelectOptionWithData(f.Id, f.Name, f)),
        ContactFields: (state) => state.AllContactFields.filter((x) => x.Name),
        VisitedSteps: (state): VisitedSteps => {
            const steps: VisitedSteps = {
                Design: false,
                Form: false,
                Settings: false,
                Publish: false,
            };
            if (!state.CurrentPageId) return steps;
            const currentPage = state.Pages.find((p) => p.Id == state.CurrentPageId);
            if (currentPage) {
                steps.Form = currentPage.FormFields.length > 0;
                steps.Settings = currentPage!.SettingsStepVisited;
                steps.Design = currentPage!.DesignStepVisited;
                steps.Publish = !_.isEmpty(currentPage!.Link);
            }
            return steps;
        },
        BatchStatusMap: ({ BatchStatus }) => {
            return _.chain(BatchStatus)
                .mapKeys((x) => x.id)
                .mapValues((x) => x.text)
                .value();
        },
        GetBatchStatusEnum:
            (_, { BatchStatusMap }) =>
            (status: string) => {
                return BatchStatusMap[status] || status;
            },
        AllFilterOperation: (state): any => state.FilterOperation_DependsOnFieldType,
        GetPagePreview:
            ({ BlobEndPoint }) =>
            (id: string) =>
                BlobEndPoint + 'pages/' + id + '/preview.jpg',
        GetEmailPreview:
            ({ BlobEndPoint }) =>
            (id: string) =>
                BlobEndPoint + 'mails/' + id + '/preview.jpg',
        CustomListsValues: (state) => state.CustomListsValues,
    },
    mutations: {
        UpdateUserData(state, data: StoreInitData): void {
            state.Pages = data.Pages;
            state.UserFullName = data.UserName;
            state.UserId = data.UserId;
        },
        UpdateContactFields(state, newMapping: ContactFieldMapping[]): void {
            state.AllContactFields = newMapping;
        },
        DeleteProject(state, pageId: string): void {
            if (pageId == state.CurrentPageId) state.CurrentPageId = '';
            state.Pages = state.Pages.filter((x) => x.Id != pageId);
        },
        DeleteNewsletter(state, id: string): void {
            state.Newsletters = state.Newsletters.filter((x) => x.Id != id);
        },
        UpdateLang(state, lang: string): void {
            state.Lang = lang;
            state.GeneralContactFields = translateListWithData(generalFields, 'enums.ContactFilterFieldEnum');
            state.FieldTypes = translateList(fieldTypes, 'enums.FieldTypeEnum');
            state.Genders = translateList(genders, 'enums.GenderEnum');
            state.FilterOperation_DependsOnFieldType = _.mapValues(operationsPerType, (x) =>
                translateListPrefix(x, 'enums.ContactFilterOperationEnum')
            );
            state.BatchStatus = translateList(
                Object.keys(Enums.BatchStatus).map((x) => new SelectOption(x, x)),
                'enums.BatchStatus'
            );
        },
        UpdateIsAuthenticated(state, IsAuthenticated: boolean): void {
            state.IsAuthenticated = IsAuthenticated;
        },
        UpdateUserPage(state, page: Project): void {
            const index = state.Pages.findIndex((x) => x.Id == page.Id);
            state.Pages.splice(index, 1, page);
        },
        UpdatePageProperties(state, updatePage: UpdatePageProperties): void {
            const page = state.Pages.find((x) => x.Id == updatePage.PageId) as any;
            if (page) {
                updatePage.Properties.forEach((pageProperty) => {
                    page[pageProperty.Property] = pageProperty.Value;
                });
            }
        },
        UpdateNewsletterProperties(state, updateNewsletter: UpdatePageProperties): void {
            const newsletter = state.Newsletters.find((x) => x.Id == updateNewsletter.PageId) as any;
            if (newsletter) {
                updateNewsletter.Properties.forEach((newsletterProperty) => {
                    newsletter[newsletterProperty.Property] = newsletterProperty.Value;
                });
            }
        },
        AddNewPage(state, project: Project): void {
            state.Pages.push(project);
        },
        AddNewNewsletter(state, newsletter: Newsletter): void {
            state.Newsletters.push(newsletter);
        },
        DeleteEmailProfile({ EmailProfiles }, id: number): void {
            const i = EmailProfiles.findIndex((x) => x.id == id);
            if (i < 0) return;

            EmailProfiles.splice(i, 1);
        },
        UpdateEmailProfile({ EmailProfiles }, x: RefData.EmailProfileObject): void {
            const item = new SelectOptionWithData(x.Id, x.Name, x);
            const i = EmailProfiles.findIndex((x) => x.id == item.id);
            if (i < 0) {
                EmailProfiles.push(item);
            } else {
                EmailProfiles.splice(i, 1, item);
            }
        },
        UpdateSMSProfile({ SMSProfiles }, x: RefData.SMSProfileObject): void {
            const item = new SelectOptionWithData(x.Id, x.Name, x);
            const i = SMSProfiles.findIndex((x) => x.id == item.id);
            if (i < 0) {
                SMSProfiles.push(item);
            } else {
                SMSProfiles.splice(i, 1, item);
            }
        },
        DeleteSMSProfile({ SMSProfiles }, id: number): void {
            const i = SMSProfiles.findIndex((x) => x.id == id);
            if (i < 0) return;

            SMSProfiles.splice(i, 1);
        },
        AddNewTag(state, tag: SelectOption): void {
            state.Tags.push(tag);
        },
        AddNewCustomListValue(state, value: CustomListValueSaveResource): void {
            const valueOption = new SelectOptionWithData(value.Id, value.Value, value);
            state.CustomListsValues.push(valueOption);
        },
    },
    actions: {
        Init({ state, dispatch }, data: InitResponse): void {
            state.Pages = data.Pages;
            state.Newsletters = data.Newsletters;
            state.UserFullName = data.UserFullName;
            state.UserId = data.UserId;
            state.Lang = data.Lang;
            state.IsAuthenticated = data.IsAuthenticated;
            state.ImportSwaggerDataStructures = data.ImportSwaggerDataStructures;
            state.BlobEndPoint = data.BlobEndPoint;
            state.BuildVersion = data.BuildVersion;

            state.AllContactFields = data.ContactFields || [];

            state.EmailProfiles = data.EmailProfiles?.map((x) => new SelectOptionWithData(x.Id, x.Name, x)) || [];
            state.SMSProfiles = data.SMSProfiles?.map((x) => new SelectOptionWithData(x.Id, x.Name, x)) || [];
            state.Groups = data.Groups?.map((x) => new SelectOption(x.Id, x.Name)) || [];
            state.Automations = data.Automations?.map((x) => new SelectOption(x.Id, x.Name)) || [];
            state.Countries = data.Countries?.map((x) => new SelectOption(x.Id, x.Name)) || [];
            state.SmsTemplates = data.SmsTemplates || [];
            state.Tags = data.Tags?.map((x) => new SelectOption(x.Id, x.Name)) || [];
            state.CustomListsValues = data.CustomListsValues?.map((x) => new SelectOptionWithData(x.Id, x.Value, x)) || [];

            if (data.IsAuthenticated) {
                const jobPrefix = `Export-${data.UserId}--`;

                state.ExportJobs = Object.keys(localStorage)
                    .filter((key) => key.startsWith(jobPrefix))
                    .map((key) =>
                        Object.assign(new ExportStatusObject('', ExportType.Default, data.UserId), JSON.parse(localStorage[key]))
                    );

                state.ExportJobs.filter((x) => x.Id && !x.Message && !x.ReportUrl).forEach((x) => {
                    dispatch('StartTrackingExportJob', x.Id);
                });
            }
        },
        StartTrackingExportJob({ state }, taskId) {
            const jobRow = state.ExportJobs.find((x) => x.Id == taskId);
            if (!jobRow) return;
            Helper.waitForExportTaskSilent(jobRow, (url, error) => {
                var job = state.ExportJobs.find((x) => x.Id == taskId);
                if (!job) return;

                if (url) {
                    job.ReportUrl = url;
                } else {
                    job.Message = error || 'Error';
                    // job.Message = error || (i18n.global.t('export.message.Default.error') as string);

                    //Swal.fire(job.Message, '', 'warning');
                }
                job.UpdateStorage();
            });
        },
        clearExportJob({ state }, taskId) {
            var job = state.ExportJobs.find((z) => z.Id == taskId);
            if (!job) return;
            localStorage.removeItem(job.Key());
            state.ExportJobs.splice(state.ExportJobs.indexOf(job), 1);
        },
        async ExportEmailDetail({ state, dispatch }, id: number) {
            Swal.showLoading();
            var resp = await api.EmailExportHistoryDetail(id);
            await Q.delay(500);
            Swal.close();
            if (resp.Data) {
                var job = new ExportStatusObject(resp.Data, ExportType.EmailDetail, state.UserId);
                state.ExportJobs.push(job);
                job.UpdateStorage();
                dispatch('StartTrackingExportJob', job.Id);
            }
        },
        async ExportSMSDetail({ state, dispatch }, id: number) {
            Swal.showLoading();
            var resp = await api.SMSExportHistoryDetail(id);
            await Q.delay(500);
            Swal.close();
            if (resp.Data) {
                var job = new ExportStatusObject(resp.Data, ExportType.SMSDetail, state.UserId);
                state.ExportJobs.push(job);
                job.UpdateStorage();
                dispatch('StartTrackingExportJob', job.Id);
            }
        },
        async ExportContacts({ state, dispatch }, filter: ContactFilter) {
            Swal.showLoading();
            var resp = await api.ExportContacts(filter);
            await Q.delay(500);
            Swal.close();
            if (resp.Data) {
                var job = new ExportStatusObject(resp.Data, ExportType.Contacts, state.UserId);
                state.ExportJobs.push(job);
                job.UpdateStorage();
                dispatch('StartTrackingExportJob', job.Id);
            }
        },
    },
};

setTimeout(() => {
    if (storeState) {
        storeState.CurrentDate = new Date();
    }
}, 60000);
